import {
  ColDef,
  GridOptions,
  Module,
  RowDataChangedEvent,
} from '@ag-grid-community/core'
import { FiltersToolPanelModule } from '@ag-grid-enterprise/filter-tool-panel'
import { ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core'
import {
  disableChangeDetectionOnScroll,
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  getFilterOnCellContextMenu,
  heliosClientSideRowConfig,
  heliosDefaultColWidth,
} from '@helios/ag-grid-config'
import { formatDates } from '@helios/common-api'
import { DensityLevel, UserPreferencesService } from '@helios/user-preferences'
import { NfColumnTypes } from '@northfork/ag-grid'
import { composeFieldPath } from '@northfork/basics'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { DividendHistory } from '../../models/dividend-history.model'

const dividendHistoryHistoryPath = composeFieldPath([] as DividendHistory[])

@Component({
  selector: 'hs-dividend-history-grid',
  templateUrl: './dividend-history-grid.component.html',
})
export class DividendHistoryGridComponent implements OnDestroy {
  @Input() data: DividendHistory[] | null = null

  readonly gridModules: Module[] = [FiltersToolPanelModule]
  readonly gridOptions: GridOptions = this.createGridOptions()
  readonly destroySubject$ = new Subject<void>()

  constructor(
    readonly changeDetector: ChangeDetectorRef,
    readonly preferences: UserPreferencesService,
  ) {}

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
  }
  createGridOptions(): GridOptions {
    return {
      columnDefs: this.createColumnDefs(),
      getContextMenuItems: params => getFilterOnCellContextMenu(params),
      ...heliosClientSideRowConfig,
      onRowDataChanged: event => this.onRowDataChanged(event),
      ...disableChangeDetectionOnScroll(this.changeDetector),
      onGridReady: params => {
        enableHeliosCopyBehavior(params.api)
        this.preferences
          .getDensityLevel()
          .pipe(takeUntil(this.destroySubject$))
          .subscribe(level =>
            enableHeliosGridDensityLevel(
              params.api,
              DensityLevel.Compact === level,
            ),
          )
      },
    }
  }

  // @internal
  createColumnDefs(): ColDef[] {
    return [
      {
        field: dividendHistoryHistoryPath('securityId'),
        headerName: 'Security ID',
        type: NfColumnTypes.Identifier,
        width: heliosDefaultColWidth.Id,
      },
      {
        field: dividendHistoryHistoryPath('declarationDate'),
        type: NfColumnTypes.Date,
        width: heliosDefaultColWidth.DateCol,
        valueFormatter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yy')
        },
        tooltipValueGetter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yyyy')
        },
      },
      {
        field: dividendHistoryHistoryPath('exDate'),
        type: NfColumnTypes.Date,
        width: heliosDefaultColWidth.DateCol,
        valueFormatter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yy')
        },
        tooltipValueGetter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yyyy')
        },
      },
      {
        field: dividendHistoryHistoryPath('payDate'),
        type: NfColumnTypes.Date,
        width: heliosDefaultColWidth.DateCol,
        valueFormatter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yy')
        },
        tooltipValueGetter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yyyy')
        },
      },
      {
        field: dividendHistoryHistoryPath('dividend'),
        type: NfColumnTypes.Identifier,
        headerName: 'Dividend Per Share',
      },
      {
        field: dividendHistoryHistoryPath('frequency'),
      },
      {
        field: dividendHistoryHistoryPath('currencyCode'),
        headerName: 'Announced DVD Currency Code',
      },
      {
        field: dividendHistoryHistoryPath('paymentCurrencyCode'),
        headerName: 'Payment Currency Code',
      },
      {
        field: dividendHistoryHistoryPath('conversionRate'),
        type: NfColumnTypes.Number,
        headerName: 'Conversion Rate',
      },
      {
        field: dividendHistoryHistoryPath('convertedPaymentPerShare'),
        type: NfColumnTypes.Number,
        headerName: 'Converted Payment Per Share',
      },
    ]
  }

  onRowDataChanged(event: RowDataChangedEvent) {
    if (this.data != null) {
      return
    }
    event.api.showLoadingOverlay()
  }
}
